import React, { useContext } from 'react';
import { Icon, MessageBar, MessageBarType, Stack, Text } from '@fluentui/react';
import { ResetFileHash } from './Endpoints/ResetFileHash';
import { ActivateArtifact } from './Endpoints/ActivateArtifact';
import { DeactivateArtifact } from './Endpoints/DeactivateArtifact';
import { RequeueDeadLetterMessages } from './Endpoints/RequeueDeadLetterMessages';
import { ReplayMessages } from './Endpoints/ReplayMessages';
import { ClearCache } from './Endpoints/ClearCache';
import { ManageHydrationCacheContainer } from './Endpoints/ManageHydrationCacheContainer';
import { SreTriage } from './Endpoints/SreTriage/SreTriage';
import { SrxBundles } from './Endpoints/SrxBundles';
import { CheckHashInStorage } from './Endpoints/CheckHashInStorage';
import { ThemeContext } from '../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';
import { useIsMobile } from '../../../Common/hooks/useIsMobile';
import { useSreAccess } from '../../../Common/hooks/useSreAccess';
import { useUrlParams } from '../../../Common/hooks/useUrlState';
import {
    GlobalConfigProperties,
    getConfig,
} from '../../../Common/api/ABHub/ABHubGlobalConfig';

interface IAdminEndpointsProps {}

interface EndpointOption {
    key: string;
    text: string;
    description: string;
    icon: string;
}

interface EndpointSection {
    title: string;
    endpoints: EndpointOption[];
}

export const AdminEndpoints: React.FC<IAdminEndpointsProps> = (
    props: IAdminEndpointsProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const isMobile = useIsMobile();

    const params = useUrlParams();
    const requestedEndpoint =
        params.getString('endpoint') ??
        (params.getString('sreTab') || params.getString('sreDetail')
            ? 'sreTriage'
            : undefined);
    const selectedEndpointKey =
        requestedEndpoint === 'sreHealthCheck' ||
        requestedEndpoint === 'sreInflightProgress'
            ? 'sreTriage'
            : requestedEndpoint;

    const stackTokens = { childrenGap: 16 };

    // Get cloud from config to determine which sections to show
    const cloud = getConfig(GlobalConfigProperties.Cloud);

    const sreAccess = useSreAccess();
    const isDevEnvironment = sreAccess.isDevEnvironment;
    const showSreEndpoints = sreAccess.hasAccess;

    const hydrationEndpoints: EndpointOption[] = [
        {
            key: 'resetFileHash',
            text: 'Reset File Hash',
            description: 'Reset the hash value for a specific file',
            icon: 'Refresh',
        },
        {
            key: 'clearCache',
            text: 'Clear Cache',
            description: 'Clear all cached files for an environment',
            icon: 'ClearSelection',
        },
        {
            key: 'manageHydrationCacheContainer',
            text: 'Manage Cache Container',
            description:
                'Delete and recreate the hydration cache Cosmos container',
            icon: 'Database',
        },
        {
            key: 'activateArtifact',
            text: 'Activate Artifact',
            description: 'Activate an artifact for processing',
            icon: 'ReceiptCheck',
        },
        {
            key: 'deactivateArtifact',
            text: 'Deactivate Artifact',
            description: 'Deactivate an artifact from processing',
            icon: 'ReceiptUndelivered',
        },
        {
            key: 'requeueDeadLetterMessages',
            text: 'Requeue Dead Letter Messages',
            description: 'Requeue messages from dead letter queue',
            icon: 'SyncOccurence',
        },
        {
            key: 'replayMessages',
            text: 'Replay Messages',
            description:
                'Replay air gap messages from a dgrep CSV export of MessageIds',
            icon: 'Rocket',
        },
    ];

    const sreEndpoints: EndpointOption[] = [
        {
            key: 'sreTriage',
            text: 'SRE Triage',
            description:
                'Build and deployment health, progress, and validation findings',
            icon: 'Timeline',
        },
    ];

    const storageEndpoints: EndpointOption[] = [
        {
            key: 'checkHashInStorage',
            text: 'Blob Triage',
            description:
                'Verify whether build hashes exist in the configured transfer containers',
            icon: 'DocumentSearch',
        },
    ];

    const srxEndpoints: EndpointOption[] = [
        {
            key: 'srxBundles',
            text: 'SRX Bundles',
            description:
                'Browse and download SRX state bundles from blob storage',
            icon: 'ZipFolder',
        },
    ];

    // SRX bundle storage (srmsaprodhs / srmsaprodmockhs) lives in Public cloud,
    // so the SRX Endpoints page is only useful where that storage account is
    // reachable: Public cloud (prod + prodmock) and dev/test environments.
    // Note: checkValidCloudOrEnvironment rejects Public cloud, so don't use it here.
    const showSrxEndpoints = cloud === 'Public' || isDevEnvironment;

    // Build sections array - only include SRE section if in sovereign cloud
    const endpointSections: EndpointSection[] = [
        {
            title: 'Hydration Endpoints',
            endpoints: hydrationEndpoints,
        },
        {
            title: 'Storage Endpoints',
            endpoints: storageEndpoints,
        },
        ...(showSreEndpoints
            ? [
                  {
                      title: 'SRE Endpoints',
                      endpoints: sreEndpoints,
                  },
              ]
            : []),
        ...(showSrxEndpoints
            ? [
                  {
                      title: 'SRX Endpoints',
                      endpoints: srxEndpoints,
                  },
              ]
            : []),
    ];

    // Flatten all endpoints for lookup
    const allEndpoints = [
        ...hydrationEndpoints,
        ...storageEndpoints,
        ...(showSreEndpoints ? sreEndpoints : []),
        ...(showSrxEndpoints ? srxEndpoints : []),
    ];

    const handleEndpointSelect = (key: string) => {
        params.setParams({ endpoint: key });
    };

    const renderEndpointComponent = () => {
        if (
            !allEndpoints.some(
                (endpoint) => endpoint.key === selectedEndpointKey
            )
        )
            return null;
        switch (selectedEndpointKey) {
            case 'sreTriage':
                return <SreTriage />;
            case 'srxBundles':
                return <SrxBundles />;
            case 'resetFileHash':
                return <ResetFileHash />;
            case 'clearCache':
                return <ClearCache />;
            case 'manageHydrationCacheContainer':
                return <ManageHydrationCacheContainer />;
            case 'activateArtifact':
                return <ActivateArtifact />;
            case 'deactivateArtifact':
                return <DeactivateArtifact />;
            case 'requeueDeadLetterMessages':
                return <RequeueDeadLetterMessages />;
            case 'replayMessages':
                return <ReplayMessages />;
            case 'checkHashInStorage':
                return <CheckHashInStorage />;
            default:
                return null;
        }
    };

    const renderEndpointSection = (section: EndpointSection) => (
        <Stack
            key={section.title}
            tokens={{ childrenGap: 8 }}
            styles={{ root: { marginBottom: '16px' } }}
        >
            <Text
                variant="medium"
                styles={{
                    root: {
                        fontWeight: 600,
                        marginBottom: '8px',
                        color: theme.palette.themeDark,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '12px',
                    },
                }}
            >
                {section.title}
            </Text>
            {section.endpoints.map((endpoint) => (
                <button
                    type="button"
                    key={endpoint.key}
                    onClick={() => handleEndpointSelect(endpoint.key)}
                    aria-pressed={selectedEndpointKey === endpoint.key}
                    style={{
                        padding: '12px',
                        width: '100%',
                        textAlign: 'left',
                        color: 'inherit',
                        font: 'inherit',
                        backgroundColor:
                            selectedEndpointKey === endpoint.key
                                ? theme.palette.neutralTertiaryAlt
                                : theme.palette.neutralLighter,
                        borderWidth: '1px 1px 1px 4px',
                        borderStyle: 'solid',
                        borderColor:
                            selectedEndpointKey === endpoint.key
                                ? theme.palette.themePrimary
                                : `${theme.palette.neutralTertiaryAlt} ${theme.palette.neutralTertiaryAlt} ${theme.palette.neutralTertiaryAlt} transparent`,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                        if (selectedEndpointKey !== endpoint.key) {
                            e.currentTarget.style.backgroundColor =
                                theme.palette.neutralLighterAlt;
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (selectedEndpointKey !== endpoint.key) {
                            e.currentTarget.style.backgroundColor =
                                theme.palette.neutralLighter;
                        }
                    }}
                >
                    <Stack
                        horizontal
                        verticalAlign="center"
                        tokens={{ childrenGap: 12 }}
                    >
                        <Icon
                            iconName={endpoint.icon}
                            styles={{
                                root: {
                                    fontSize: 20,
                                    color:
                                        selectedEndpointKey === endpoint.key
                                            ? theme.palette.themePrimary
                                            : theme.palette.neutralSecondary,
                                },
                            }}
                        />
                        <Stack>
                            <Text
                                variant="medium"
                                styles={{
                                    root: {
                                        fontWeight:
                                            selectedEndpointKey === endpoint.key
                                                ? 600
                                                : 400,
                                        color:
                                            selectedEndpointKey === endpoint.key
                                                ? theme.palette.black
                                                : theme.palette.neutralPrimary,
                                    },
                                }}
                            >
                                {endpoint.text}
                            </Text>
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralSecondary,
                                    },
                                }}
                            >
                                {endpoint.description}
                            </Text>
                        </Stack>
                    </Stack>
                </button>
            ))}
        </Stack>
    );

    return (
        <div className="AdminEndpoints-root">
            {!selectedEndpointKey && (
                <MessageBar
                    messageBarType={MessageBarType.info}
                    styles={{
                        root: {
                            backgroundColor: theme.palette.themePrimary,
                            marginBottom: '16px',
                        },
                        text: {
                            color: theme.palette.white,
                            fontWeight: 500,
                        },
                        icon: {
                            color: theme.palette.white,
                        },
                    }}
                >
                    Select an endpoint from the list below to execute admin
                    operations
                </MessageBar>
            )}

            <Stack
                horizontal={!isMobile}
                tokens={stackTokens}
                styles={{
                    root: {
                        marginTop: selectedEndpointKey ? '0' : '16px',
                        minWidth: 0,
                        selectors: {
                            '@media (max-width: 768px)': {
                                flexDirection: 'column',
                            },
                        },
                    },
                }}
            >
                {/* Left Panel: Endpoint List with Sections */}
                <Stack
                    styles={{
                        root: {
                            width: isMobile ? '100%' : '300px',
                            flexShrink: 0,
                            selectors: {
                                '@media (max-width: 768px)': {
                                    width: '100%',
                                },
                            },
                        },
                    }}
                    tokens={{ childrenGap: 0 }}
                >
                    <Text
                        variant="mediumPlus"
                        styles={{
                            root: { fontWeight: 600, marginBottom: '12px' },
                        }}
                    >
                        Available Endpoints
                    </Text>
                    {endpointSections.map((section) =>
                        renderEndpointSection(section)
                    )}
                </Stack>

                {/* Right Panel: Selected Endpoint Form */}
                {selectedEndpointKey && (
                    <Stack
                        styles={{
                            root: {
                                flex: 1,
                                minWidth: 0,
                                boxSizing: 'border-box',
                                marginLeft: isMobile ? '0' : '16px',
                                marginTop: isMobile ? '16px' : '0',
                                padding: isMobile ? '16px' : '20px',
                                backgroundColor:
                                    theme.palette.neutralLighterAlt,
                                borderRadius: '4px',
                                border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                selectors: {
                                    '@media (max-width: 768px)': {
                                        width: '100%',
                                        marginLeft: 0,
                                        marginTop: 16,
                                        padding: 16,
                                    },
                                },
                            },
                        }}
                        tokens={stackTokens}
                    >
                        <Stack
                            horizontal
                            verticalAlign="center"
                            horizontalAlign="space-between"
                        >
                            <Stack
                                horizontal
                                verticalAlign="center"
                                tokens={{ childrenGap: 12 }}
                            >
                                <Icon
                                    iconName={
                                        allEndpoints.find(
                                            (e) => e.key === selectedEndpointKey
                                        )?.icon
                                    }
                                    styles={{
                                        root: {
                                            fontSize: 24,
                                            color: theme.palette.themePrimary,
                                        },
                                    }}
                                />
                                <Text
                                    as={
                                        selectedEndpointKey === 'sreTriage'
                                            ? 'h2'
                                            : 'span'
                                    }
                                    variant="xLarge"
                                    styles={{
                                        root: { fontWeight: 600, margin: 0 },
                                    }}
                                >
                                    {
                                        allEndpoints.find(
                                            (e) => e.key === selectedEndpointKey
                                        )?.text
                                    }
                                </Text>
                            </Stack>
                        </Stack>

                        <div
                            style={{
                                marginTop:
                                    selectedEndpointKey === 'sreTriage'
                                        ? 0
                                        : '16px',
                            }}
                        >
                            {renderEndpointComponent()}
                        </div>
                    </Stack>
                )}
            </Stack>
        </div>
    );
};
